import classnames from 'classnames'
import * as React from 'react'
import type {TSubmitMinorActionResponse} from '@kapitalist/contract/endpoints'
import {paymentPropriety} from '@kapitalist/contract/gameplay/payment'
import type {TRuleset} from '@kapitalist/contract/ruleset'
import {valueOfCards} from '@kapitalist/contract/ruleset'
import {show$} from '@kapitalist/contract/utils/millions/copy'
import * as S from '@kapitalist/utils/Set'
import ProvideForm from '~/components/ProvideForm/normal'
import {FormRadioButtonGroup, RadioButton} from '~/components/RadioButtonGroup'
import {SingleButtonForm} from '~/components/SingleButtonForm'
import {FormToggleGroup} from '~/components/ToggleGroup'
import DSButton from '~/design-system/Button'
import DSText, {DSTextDiv} from '~/design-system/DSText'
import {DSLinkText} from '~/design-system/Link'
import type {TSkin} from '~/game/skins'
import {getObviousPayment, groupedCardDisplayNames} from '~/game/utils'
import type {PlayerGamePosition} from '~/models/game/GamePosition'
import type InProgressMajorAction from '~/models/game/InProgressMajorAction'
import {unambiguousNeighborhoodDescription} from '~/models/game/MajorActions/utils'
import {
  neighborhoodToAcceptForcedDealDestination,
  possibleDestinationNeighborhoodsForForcedDeal,
} from '~/models/game/MinorActionPrompts/MinorActionPromptForcedDeal'
import MinorActionAcceptForcedDeal from '~/models/game/MinorActions/MinorActionAcceptForcedDeal'
import MinorActionPaySomething from '~/models/game/MinorActions/MinorActionPaySomething'
import type {AnyMinorAction, AnyMinorActionPaySomething} from '~/models/game/types'
import styles from './styles.module.css'

type TAcceptForcedDealForm = {destinationPNId: string}

interface IProps {
  chooseMinorAction: (
    minorAction: AnyMinorAction
  ) => Promise<TSubmitMinorActionResponse>
  chooseMinorActionSubmitSuccess: (response: TSubmitMinorActionResponse) => void
  skin: TSkin
  rules: TRuleset
  playerGamePosition: PlayerGamePosition
  inProgressMajorAction: InProgressMajorAction
  selectedCardIds: Set<string>
}

interface IState {
  payForMinorAction?: AnyMinorActionPaySomething
  destinationForAcceptedForcedDeal?: MinorActionAcceptForcedDeal
  gamePositionHash?: string
}

export default class PendingMinorActionUI extends React.Component<IProps, IState> {
  state: IState = {}

  // don't make the user click the only action if it requires further input
  static getDerivedStateFromProps(
    nextProps: IProps,
    prevState: IState
  ): Partial<IState> | null {
    const {playerGamePosition, inProgressMajorAction, rules} = nextProps

    if (playerGamePosition.hash === prevState.gamePositionHash) {
      // already set this default once, don't override player's wishes anymore
      return null
    }
    const retVal: Partial<IState> = {
      gamePositionHash: playerGamePosition.hash,
    }

    const {currentPlayer} = playerGamePosition
    const currentPlayerId = currentPlayer.id

    // you can see your options for a minor action you're going to be asked about soon ('eventual'),
    // but you can only act on it if it is your turn to act (`GamePosition.isMyMinorTurn`)
    const myEventualPendingMinorAction =
      inProgressMajorAction?.pendingMinorActions.find(
        pma => pma.minorActorPlayerId === currentPlayerId
      )

    const validResponses =
      myEventualPendingMinorAction?.validResponses({
        portfolio: currentPlayer.portfolio,
        gamePosition: playerGamePosition,
        inProgressMajorAction,
        hand: currentPlayer.hand,
        rules,
      }) ?? []

    if (validResponses.length === 1) {
      const r = validResponses[0]
      if (r instanceof MinorActionPaySomething) {
        return {...retVal, payForMinorAction: r}
      } else if (r instanceof MinorActionAcceptForcedDeal) {
        return {...retVal, destinationForAcceptedForcedDeal: r}
      }
    }
    return retVal
  }

  renderPayForMinorAction = (payForMinorAction: AnyMinorActionPaySomething) => {
    const {playerGamePosition, inProgressMajorAction, selectedCardIds, skin} =
      this.props
    const {currentPlayer} = playerGamePosition
    const currentPlayerId = currentPlayer.id

    const properPaymentData = paymentPropriety(
      payForMinorAction.millionsOwed,
      currentPlayer.portfolio.allValuableCards(),
      selectedCardIds
    )

    const obviousPayment = getObviousPayment(payForMinorAction, playerGamePosition)

    return (
      <div
        className={classnames(
          styles.minorActionPrompt
          // this.state.submittingMinorActionReply && styles.loading
        )}
      >
        <DSTextDiv size="body-16" bold>
          <DSLinkText
            size="body-16"
            underline="onHover"
            color="blue"
            onClick={() => this.setState({payForMinorAction: undefined})}
          >
            &lt;
          </DSLinkText>{' '}
          You've chosen to pay.
          <br />
          <br />
          {obviousPayment && (
            <>
              <SingleButtonForm
                onSubmit={() => {
                  payForMinorAction.paidWithCards = obviousPayment.paidWith
                  return this.props.chooseMinorAction(payForMinorAction)
                }}
                submitSuccess={this.props.chooseMinorActionSubmitSuccess}
                size="15px"
                buttonStyle="primary"
                disabled={
                  inProgressMajorAction.pendingMinorActions[0]
                    ?.minorActorPlayerId !== currentPlayerId
                }
              >
                Make Suggested Payment (
                {groupedCardDisplayNames(obviousPayment.paidWith, skin).join(', ')})
              </SingleButtonForm>
              <br />
              Or,{' '}
            </>
          )}
          {obviousPayment ? 's' : 'S'}elect cards in front of you on the table to
          make a payment with.
          <br />
          {properPaymentData.type !== 'proper' && S.nonempty(selectedCardIds) && (
            <span>
              <br />
              The selected cards don't constitute a valid payment.{' '}
              {properPaymentData.type === 'tooManyCards' &&
                `There are too many cards selected (${show$(
                  properPaymentData.currentPayment
                )}).`}
              {properPaymentData.type === 'moreMoneyOwed' &&
                `There are too few cards selected (${show$(
                  properPaymentData.currentPayment
                )}).`}
              {properPaymentData.type === 'invalidCardSelected' &&
                `You've chosen an invalid card for payment.`}
            </span>
          )}
          {properPaymentData.type === 'proper' && (
            <SingleButtonForm
              onSubmit={() => {
                payForMinorAction.paidWithCards = properPaymentData.paidWith
                return this.props.chooseMinorAction(payForMinorAction)
              }}
              submitSuccess={this.props.chooseMinorActionSubmitSuccess}
              size="15px"
              buttonStyle="primary"
              disabled={
                inProgressMajorAction.pendingMinorActions[0]?.minorActorPlayerId !==
                currentPlayerId
              }
            >
              Pay {show$(valueOfCards(properPaymentData.paidWith))} (
              {groupedCardDisplayNames(properPaymentData.paidWith, skin).join(', ')})
            </SingleButtonForm>
          )}
        </DSTextDiv>
      </div>
    )
  }

  // TODO maybe refactor this out into the MinorActionAcceptForcedDeal class - is there an appropriate abstract method
  //   to make on MinorAction/MinorActionPrompt like `availableMovesFromHand` to handle this and other minor actions?
  renderDestinationForAcceptedForcedDeal = (
    acceptedForcedDeal: MinorActionAcceptForcedDeal
  ) => {
    const {playerGamePosition, inProgressMajorAction, skin} = this.props
    const {currentPlayer} = playerGamePosition
    const currentPlayerId = currentPlayer.id
    const offeredCard = acceptedForcedDeal.offeredCard

    const targetNeighborhoods = possibleDestinationNeighborhoodsForForcedDeal({
      offeredCard,
      requestedCard: acceptedForcedDeal.requestedCard,
      targetedPlayer: currentPlayer,
    })

    // A dual whose both colors would each start a new set collapses to one option here (they
    // share the BY_ITSELF id), so V1 lands it as the first color and offers no per-color choice.
    const allChoices = S.create(targetNeighborhoods.map(pn => pn.id))
    const humanChoices = Object.fromEntries(
      targetNeighborhoods.map(pn => [
        pn.id,
        unambiguousNeighborhoodDescription(
          {skin, gamePosition: playerGamePosition},
          pn.id
        ),
      ])
    )

    return (
      <div
        className={classnames(
          styles.minorActionPrompt
          // this.state.submittingMinorActionReply && styles.loading
        )}
      >
        <DSTextDiv size="body-16" bold>
          <DSLinkText
            size="body-16"
            underline="onHover"
            color="blue"
            onClick={() =>
              this.setState({destinationForAcceptedForcedDeal: undefined})
            }
          >
            &lt;
          </DSLinkText>{' '}
          You've chosen to accept the forced deal.
          <br />
          Select where the card you are receiving will go.
          <br />
          <br />
          {inProgressMajorAction.pendingMinorActions[0]?.minorActorPlayerId ===
            currentPlayerId && (
            <ProvideForm<TAcceptForcedDealForm, TSubmitMinorActionResponse>
              onSubmit={formState => {
                // constraints validate this assertion
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const destinationPNId = FormToggleGroup.formValueToFirstSelection(
                  formState.destinationPNId
                )!
                // the form's choices are these neighborhoods, so the selection is always present
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const selectedNeighborhood = targetNeighborhoods.find(
                  pn => pn.id === destinationPNId
                )!
                acceptedForcedDeal.datasource.destination =
                  neighborhoodToAcceptForcedDealDestination(selectedNeighborhood)
                return this.props.chooseMinorAction(acceptedForcedDeal)
              }}
              submitSuccess={(_, r) => this.props.chooseMinorActionSubmitSuccess(r)}
              toFormFields={(generateFormFieldProps, formProps) => (
                <div className={styles['radio-list']}>
                  <FormRadioButtonGroup<string, TAcceptForcedDealForm>
                    allChoices={allChoices}
                    initialChoice={
                      allChoices.size === 1 ? S.first(allChoices) : undefined
                    }
                    allowDeselection
                    generateFormFieldProps={generateFormFieldProps}
                    formProps={formProps}
                    fieldName="destinationPNId"
                    humanChoices={humanChoices}
                    renderRadioButton={radioButtonProps => (
                      <RadioButton
                        key={radioButtonProps.choice}
                        {...radioButtonProps}
                        toggleStyle="radio"
                        classNames={{
                          choice: styles['radio-card'],
                          selectedChoice: styles['selected'],
                          toggle: styles['radio-checkbox'],
                          fadedToggle: styles['faded-toggle'],
                          choiceLabel: styles['radio-label'],
                        }}
                        label={{
                          size: 'body-15',
                          fadedColor: 'pearl-grey-darkest',
                        }}
                      />
                    )}
                  />
                  <DSButton
                    onClick={() => formProps.onSubmit()}
                    appearDisabled={formProps.anyErrors}
                    size="15px"
                    buttonStyle="primary"
                    loading={formProps.isFormSubmitting}
                    disabled={formProps.isFormSubmitting}
                  >
                    Accept Forced Deal
                  </DSButton>
                </div>
              )}
            />
          )}
        </DSTextDiv>
      </div>
    )
  }

  render() {
    const {playerGamePosition, inProgressMajorAction, skin, rules} = this.props
    const {currentPlayer} = playerGamePosition
    const currentPlayerId = currentPlayer.id
    const {payForMinorAction, destinationForAcceptedForcedDeal} = this.state

    if (payForMinorAction) {
      return this.renderPayForMinorAction(payForMinorAction)
    } else if (destinationForAcceptedForcedDeal) {
      return this.renderDestinationForAcceptedForcedDeal(
        destinationForAcceptedForcedDeal
      )
    }

    // you can see your options for a minor action you're going to be asked about soon ('eventual'),
    // but you can only act on it if it is your turn to act (`GamePosition.isMyMinorTurn`)
    const myEventualPendingMinorAction =
      inProgressMajorAction?.pendingMinorActions.find(
        pma => pma.minorActorPlayerId === currentPlayerId
      )

    if (myEventualPendingMinorAction && inProgressMajorAction) {
      return (
        <div
          className={classnames(
            styles.minorActionPrompt
            // this.state.submittingMinorActionReply && styles.loading
          )}
        >
          <DSTextDiv size="body-16" bold className={styles.minorActionPromptTitle}>
            {playerGamePosition.isMyMinorTurn()
              ? 'It is your turn to act, choose your response:'
              : '(Not your turn yet, but) Consider your response:'}
          </DSTextDiv>
          <ul>
            {myEventualPendingMinorAction
              ?.validResponses({
                portfolio: currentPlayer.portfolio,
                gamePosition: playerGamePosition,
                inProgressMajorAction,
                hand: currentPlayer.hand,
                rules,
              })
              .map(minorAction => {
                const desc = minorAction.userFacingDescription({
                  gamePosition: playerGamePosition,
                  inProgressMajorAction,
                  majorActor: playerGamePosition.majorActorPlayer,
                  minorActor: currentPlayer,
                  skin,
                  tense: 'imperativePrompt',
                })
                const moreInputNeeded =
                  minorAction instanceof MinorActionPaySomething ||
                  minorAction instanceof MinorActionAcceptForcedDeal

                return (
                  <DSText
                    size="body-15"
                    tag="li"
                    key={desc}
                    className={classnames(
                      styles.actionChoice,
                      styles[minorAction.type]
                    )}
                    onClick={() => {
                      if (minorAction instanceof MinorActionPaySomething) {
                        // if PaySomething, ask what cards to pay with
                        this.setState({payForMinorAction: minorAction})
                      } else if (
                        minorAction instanceof MinorActionAcceptForcedDeal
                      ) {
                        // if AcceptForcedDeal, ask where to put the new property
                        //   see MinorActionPromptForcedDeal
                        //   see MinorActionAcceptForcedDeal.datasource.destination
                        this.setState({
                          destinationForAcceptedForcedDeal: minorAction,
                        })
                      } else {
                        // "one-click" minor actions can't be clicked unless it is your turn
                        // unlike the above that have their own UIs that we let players enter
                        // before their turn
                        if (!playerGamePosition.isMyMinorTurn()) {
                          return
                        }

                        // TODO better loading state and error handling / UI for this promise...
                        this.props
                          .chooseMinorAction(minorAction)
                          .then(r => this.props.chooseMinorActionSubmitSuccess(r))
                          .catch(() => undefined)
                      }
                    }}
                  >
                    {desc}
                    {moreInputNeeded && `...`}
                  </DSText>
                )
              })}
          </ul>
        </div>
      )
    }

    return null
  }
}
